import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { CreditCostNotice } from '../components/credits/CreditCostNotice';
import { useCreditConfirm } from '../components/credits/useCreditConfirm';
import { ContactLabelSelector } from '../components/ContactLabelSelector';
import { ContactCategorySelector } from '../components/ContactCategorySelector';

const BRAZILIAN_STATES = [
  { uf: 'AC', name: 'Acre' }, { uf: 'AL', name: 'Alagoas' }, { uf: 'AP', name: 'Amapá' },
  { uf: 'AM', name: 'Amazonas' }, { uf: 'BA', name: 'Bahia' }, { uf: 'CE', name: 'Ceará' },
  { uf: 'DF', name: 'Distrito Federal' }, { uf: 'ES', name: 'Espírito Santo' },
  { uf: 'GO', name: 'Goiás' }, { uf: 'MA', name: 'Maranhão' }, { uf: 'MT', name: 'Mato Grosso' },
  { uf: 'MS', name: 'Mato Grosso do Sul' }, { uf: 'MG', name: 'Minas Gerais' },
  { uf: 'PA', name: 'Pará' }, { uf: 'PB', name: 'Paraíba' }, { uf: 'PR', name: 'Paraná' },
  { uf: 'PE', name: 'Pernambuco' }, { uf: 'PI', name: 'Piauí' }, { uf: 'RJ', name: 'Rio de Janeiro' },
  { uf: 'RN', name: 'Rio Grande do Norte' }, { uf: 'RS', name: 'Rio Grande do Sul' },
  { uf: 'RO', name: 'Rondônia' }, { uf: 'RR', name: 'Roraima' }, { uf: 'SC', name: 'Santa Catarina' },
  { uf: 'SP', name: 'São Paulo' }, { uf: 'SE', name: 'Sergipe' }, { uf: 'TO', name: 'Tocantins' }
];

type ContactCategory = {
  id: string;
  nome: string;
};

export function GoogleMapsExtractorPage() {
  const [serperConfigured, setSerperConfigured] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  
  const [loadingNeighborhoods, setLoadingNeighborhoods] = useState(false);
  const [neighborhoods, setNeighborhoods] = useState<string[]>([]);
  const [searching, setSearching] = useState(false);
  const { request: requestSearchConfirm, modal: searchConfirmModal } = useCreditConfirm();
  const [results, setResults] = useState<any[]>([]);
  const [contactCategories, setContactCategories] = useState<ContactCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [savingContactId, setSavingContactId] = useState<string>('');
  const [savingAll, setSavingAll] = useState(false);

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers
    };
    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
    return fetch(url, {
      ...options,
      headers
    });
  };

  useEffect(() => {
    loadSerperConfig();
    loadContactCategories();
  }, []);

  useEffect(() => {
    setNeighborhoods([]);
    setNeighborhood('');
  }, [city, state]);

  const clearNeighborhoodSuggestions = () => {
    setNeighborhoods([]);
    setNeighborhood('');
  };

  const loadContactCategories = async () => {
    try {
      setLoadingCategories(true);
      console.log('[GoogleMaps] Carregando categorias de contato...');
      const response = await authenticatedFetch('/api/categorias/all');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setContactCategories(data);
      console.log(`[GoogleMaps] ${data.length} categorias carregadas`);
    } catch (error) {
      console.error('[GoogleMaps] Erro ao carregar categorias:', error);
      toast.error('Erro ao carregar categorias de contatos');
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadSerperConfig = async () => {
    try {
      console.log('[GoogleMaps] Verificando configuração Serper...');
      const response = await authenticatedFetch('/api/google-maps/serper');
      if (response.ok) {
        const data = await response.json();
        setSerperConfigured(!!data.configured);
        console.log('[GoogleMaps] Serper configurado:', !!data.configured);
      }
    } catch (error) {
      console.error('[GoogleMaps] Erro ao verificar configuração:', error);
    }
  };

  const handleSavePlaces = async (placesToSave: any[], scope: 'single' | 'all') => {
    if (!placesToSave || placesToSave.length === 0) {
      toast.error('Nenhum contato válido para salvar');
      return;
    }

    try {
      if (scope === 'all') {
        setSavingAll(true);
      } else {
        setSavingContactId(placesToSave[0]?.phoneNumber || '');
      }

      console.log(
        `[GoogleMaps] Salvando ${scope === 'all' ? `${placesToSave.length} contatos` : 'um contato'}...`
      );

      const response = await authenticatedFetch('/api/google-maps/save', {
        method: 'POST',
        body: JSON.stringify({
          places: placesToSave.map((place) => ({
            title: place.title,
            phoneNumber: place.phoneNumber,
            address: place.address
          })),
          categoryId: selectedCategoryId || null,
          tags: selectedTags
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erro ao salvar contatos');
      }

      const result = await response.json();
      toast.success(
        `Contatos salvos! ${result.imported} novos, ${result.updated} atualizados, ${result.skipped} ignorados`
      );
    } catch (error) {
      console.error('[GoogleMaps] Erro ao salvar contatos:', error);
      toast.error(
        error instanceof Error ? error.message : 'Erro ao salvar contatos extraídos do Google Maps'
      );
    } finally {
      setSavingAll(false);
      setSavingContactId('');
    }
  };

  const handleSaveSingle = (place: any) => {
    if (!place.phoneNumber) {
      toast.error('Contato sem telefone não pode ser salvo');
      return;
    }
    handleSavePlaces([place], 'single');
  };

  const handleSaveAll = () => {
    const placesWithPhone = results.filter((result) => !!result.phoneNumber);
    if (placesWithPhone.length === 0) {
      toast.error('Nenhum resultado com telefone para salvar');
      return;
    }
    handleSavePlaces(placesWithPhone, 'all');
  };

  const handleLoadNeighborhoods = async () => {
    if (!city || !state) {
      toast.error('Preencha cidade e estado primeiro');
      return;
    }
    try {
      setLoadingNeighborhoods(true);
      console.log(`[GoogleMaps] Consultando bairros de ${city}/${state}...`);
      const response = await authenticatedFetch('/api/google-maps/neighborhoods', {
        method: 'POST',
        body: JSON.stringify({ city, state })
      });
      if (!response.ok) throw new Error('Erro ao consultar bairros');
      const data = await response.json();
      setNeighborhoods(data.neighborhoods || []);
      console.log(`[GoogleMaps] ${data.neighborhoods?.length || 0} bairros encontrados`);
      toast.success(`${data.neighborhoods?.length || 0} bairros encontrados`);
    } catch (error) {
      console.error('[GoogleMaps] Erro ao consultar bairros:', error);
      toast.error('Erro ao consultar bairros via IA');
    } finally {
      setLoadingNeighborhoods(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm || !city || !state) {
      toast.error('Preencha termo de busca, cidade e estado');
      return;
    }
    if (!serperConfigured) {
      toast.error('Serper API não configurada. Solicite ao administrador do sistema.');
      return;
    }
    try {
      setSearching(true);
      const query = `${searchTerm} ${neighborhood ? neighborhood + ', ' : ''}${city}, ${state}`;
      console.log(`[GoogleMaps] Buscando: "${query}"`);
      
      const response = await authenticatedFetch('/api/google-maps/search', {
        method: 'POST',
        body: JSON.stringify({ query })
      });
      
      if (!response.ok) throw new Error('Erro ao buscar no Google Maps');
      const data = await response.json();
      setResults(data.results || []);
      console.log(`[GoogleMaps] ${data.results?.length || 0} resultados encontrados`);
      toast.success(`${data.results?.length || 0} resultados encontrados`);
    } catch (error) {
      console.error('[GoogleMaps] Erro na busca:', error);
      toast.error('Erro ao buscar no Google Maps');
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {searchConfirmModal}
      <Header title="Extrator Google Maps" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status da API */}
        {serperConfigured ? (
          <div className="bg-orange-50 border border-orange-200 rounded-lg shadow p-4 mb-6 flex items-center gap-3">
            <span className="text-xl">🟠</span>
            <p className="text-sm font-medium text-orange-800">
              Integração Google Maps (Serper) configurada. O custo por busca aparece no aviso acima do botão.
            </p>
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg shadow p-4 mb-6 flex items-center gap-3">
            <span className="text-xl">⚠️</span>
            <div>
              <p className="text-sm font-medium text-yellow-800">
                Serper API não configurada. Solicite ao administrador do sistema.
              </p>
            </div>
          </div>
        )}

        {/* Formulário de Busca */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Buscar Estabelecimentos</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Termo de Busca *</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Ex: Restaurantes, Dentistas, Academias..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Cidade *</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Ex: São Paulo"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Estado *</label>
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Selecione...</option>
                {BRAZILIAN_STATES.map(s => (
                  <option key={s.uf} value={s.uf}>{s.uf} - {s.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bairro (opcional)</label>
              <div className="flex gap-2">
                {neighborhoods.length > 0 ? (
                  <>
                    <select
                      value={neighborhood}
                      onChange={(e) => setNeighborhood(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Todos os bairros</option>
                      {neighborhoods.map((n, i) => (
                        <option key={i} value={n}>{n}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={clearNeighborhoodSuggestions}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Limpar
                    </button>
                  </>
                ) : (
                  <input
                    type="text"
                    value={neighborhood}
                    onChange={(e) => setNeighborhood(e.target.value)}
                    placeholder="Ex: Centro"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                )}
                <button
                  onClick={handleLoadNeighborhoods}
                  disabled={loadingNeighborhoods || !city || !state}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 whitespace-nowrap"
                >
                  {loadingNeighborhoods ? '🤖...' : '🤖 IA'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Consultas de bairros usam o modelo <strong>IntegraIA (cx/gpt-6-luna)</strong>.
                Integração com IA deve estar configurada.
              </p>
            </div>
          </div>

          <div className="mb-3">
            <CreditCostNotice items={[{ featureKey: 'google_extraction', qty: 1 }]} />
          </div>

          <button
            onClick={() => requestSearchConfirm({
              title: 'Extração do Google Maps',
              items: [{ featureKey: 'google_extraction', qty: 1 }],
              note: 'Cada busca consome este valor, independente da quantidade de resultados.',
              confirmLabel: 'Buscar',
              onConfirm: handleSearch,
            })}
            disabled={searching || !searchTerm || !city || !state || !serperConfigured}
            className="w-full px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {searching ? 'Buscando...' : '🔍 Buscar no Google Maps'}
          </button>
        </div>

        {/* Resultados */}
        {results.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Resultados ({results.length})</h2>
            <div className="mb-4 border rounded-lg p-4 bg-gray-50">
              <div className="flex flex-col md:flex-row md:items-end gap-4">
                <div className="flex-1">
                  <ContactCategorySelector categories={contactCategories} value={selectedCategoryId} onChange={setSelectedCategoryId} label="Categoria de contatos (opcional)" />
                  {loadingCategories && (
                    <p className="text-xs text-gray-500 mt-1">Carregando categorias...</p>
                  )}
                </div>
                <div className="flex-1"><ContactLabelSelector value={selectedTags} onChange={setSelectedTags} /></div>
                <div className="md:w-56">
                  <button
                    onClick={handleSaveAll}
                    disabled={savingAll || results.every((result) => !result.phoneNumber)}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {savingAll ? 'Salvando tudo...' : 'Salvar todos os contatos'}
                  </button>
                  <p className="text-xs text-gray-500 mt-2">
                    Apenas resultados com telefone serão salvos.
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              {results.map((result, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                  <h3 className="font-semibold text-gray-900">{result.title}</h3>
                  <p className="text-sm text-gray-600">{result.address}</p>
                  {result.phoneNumber && (
                    <p className="text-sm text-blue-600 mt-1">📞 {result.phoneNumber}</p>
                  )}
                  {result.website && (
                    <p className="text-sm text-green-600 mt-1">
                      🌐{' '}
                      <a
                        href={result.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-green-700"
                      >
                        Visitar site
                      </a>
                    </p>
                  )}
                  {result.rating && (
                    <p className="text-sm text-yellow-600 mt-1">⭐ {result.rating} ({result.reviews} avaliações)</p>
                  )}
                  <div className="mt-3">
                    <button
                      onClick={() => handleSaveSingle(result)}
                      disabled={!result.phoneNumber || savingAll || savingContactId === result.phoneNumber}
                      className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      {!result.phoneNumber
                        ? 'Sem telefone'
                        : savingContactId === result.phoneNumber
                        ? 'Salvando...'
                        : 'Salvar contato'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
